import type { Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmationModal, ConfirmDeleteModal } from '@/shared/ui';
import { DocumentSelectionModal, useDocuments } from '@/features/Document';
import type { DocumentRevision } from '@/entities/Document';
import { AddRecordModal } from '@/features/Record';
import type { RecordSelection } from '@/features/Record';
import CreateModuleModal from '../ui/CreateModuleModal.vue';
import CreateFolderModal from '../ui/CreateFolderModal.vue';
import {
  ADD_DOCUMENT_DEFAULT_AMOUNT,
  ADD_DOCUMENT_MAX_SELECTIONS,
  ADD_DOCUMENT_MODAL_COPY,
  ADD_DOCUMENT_ORDER_OPTIONS,
  ADD_DOCUMENT_SELECTION_LIMIT_MESSAGE,
} from '../config/documentLink';
import {
  ADD_RECORD_MAX_SELECTIONS,
  ADD_RECORD_SELECTION_LIMIT_MESSAGE,
} from '../config/recordLink';
import {
  PASTE_PERMISSIONS_CANCEL_BUTTON_TEXT,
  PASTE_PERMISSIONS_CONFIRM_BUTTON_TEXT,
  PASTE_PERMISSIONS_CONFIRMATION_DESCRIPTION,
  PASTE_PERMISSIONS_CONFIRMATION_TITLE,
} from '../config/messages';
import type { DirectoryTreeNode } from './types';
import type { CreateModuleSubmitPayload } from './useCreateModuleForm';
import type { CreateFolderSubmitPayload } from './useCreateFolderForm';

export interface UseDirectoriesModalsOptions {
  selectedNode: Ref<DirectoryTreeNode | null>;
  isCreateModuleSubmitting: Ref<boolean>;
  createModuleTitleError: Ref<string>;
  handleCreateModuleSubmit: (payload: CreateModuleSubmitPayload) => Promise<boolean>;
  isCreateFolderSubmitting: Ref<boolean>;
  createFolderTitleError: Ref<string>;
  handleCreateFolderSubmit: (payload: CreateFolderSubmitPayload) => Promise<boolean>;
  handleAddChild: () => boolean;
  isAddRecordSubmitting: Ref<boolean>;
  handleAddRecordSubmit: (selections: RecordSelection[]) => Promise<boolean>;
  isAddDocumentSubmitting: Ref<boolean>;
  handleAddDocumentSubmit: (revisions: DocumentRevision[]) => Promise<boolean>;
  eligibleDocumentsSearchUrl: Ref<string | null>;
  eligibleMatricesSearchUrl: Ref<string | null>;
  selectedNodeIds: Ref<string[]>;
  deleteModalTitle: Ref<string>;
  deleteModalDescription: Ref<string>;
  isDeletingSelection: Ref<boolean>;
  confirmDeleteSelection: () => Promise<boolean>;
  canPastePermissions: Ref<boolean>;
  pastePermissions: () => Promise<void>;
}

export function useDirectoriesModals(options: UseDirectoriesModalsOptions) {
  const {
    selectedNode,
    isCreateModuleSubmitting,
    createModuleTitleError,
    handleCreateModuleSubmit,
    isCreateFolderSubmitting,
    createFolderTitleError,
    handleCreateFolderSubmit,
    handleAddChild,
    isAddRecordSubmitting,
    handleAddRecordSubmit,
    isAddDocumentSubmitting,
    handleAddDocumentSubmit,
    eligibleDocumentsSearchUrl,
    eligibleMatricesSearchUrl,
    selectedNodeIds,
    deleteModalTitle,
    deleteModalDescription,
    isDeletingSelection,
    confirmDeleteSelection,
    canPastePermissions,
    pastePermissions,
  } = options;

  const { viewDocument } = useDocuments();

  const createModuleModal = useModal({
    component: CreateModuleModal,
    attrs: {
      get isSubmitting() {
        return isCreateModuleSubmitting.value;
      },
      get titleError() {
        return createModuleTitleError.value;
      },
      onClose() {
        createModuleModal.close();
      },
      onClearTitleError() {
        createModuleTitleError.value = '';
      },
      async onSubmit(payload: CreateModuleSubmitPayload) {
        const success = await handleCreateModuleSubmit(payload);
        if (success) {
          createModuleModal.close();
        }
      },
    },
  });

  const createFolderModal = useModal({
    component: CreateFolderModal,
    attrs: {
      get isSubmitting() {
        return isCreateFolderSubmitting.value;
      },
      get titleError() {
        return createFolderTitleError.value;
      },
      onClose() {
        createFolderModal.close();
      },
      onClearTitleError() {
        createFolderTitleError.value = '';
      },
      async onSubmit(payload: CreateFolderSubmitPayload) {
        const success = await handleCreateFolderSubmit(payload);
        if (success) {
          createFolderModal.close();
        }
      },
    },
  });

  const addRecordModal = useModal({
    component: AddRecordModal,
    attrs: {
      maxSelections: ADD_RECORD_MAX_SELECTIONS,
      selectionLimitMessage: ADD_RECORD_SELECTION_LIMIT_MESSAGE,
      get isSubmitting() {
        return isAddRecordSubmitting.value;
      },
      get searchUrl() {
        return eligibleMatricesSearchUrl.value;
      },
      onClose() {
        addRecordModal.close();
      },
      async onSubmit(selections: RecordSelection[]) {
        const success = await handleAddRecordSubmit(selections);
        if (success) {
          addRecordModal.close();
        }
      },
    },
  });

  const addDocumentModal = useModal({
    component: DocumentSelectionModal,
    attrs: {
      title: ADD_DOCUMENT_MODAL_COPY.title,
      tableTitle: ADD_DOCUMENT_MODAL_COPY.tableTitle,
      selectionMode: 'multiple',
      maxSelections: ADD_DOCUMENT_MAX_SELECTIONS,
      selectionLimitMessage: ADD_DOCUMENT_SELECTION_LIMIT_MESSAGE,
      confirmButtonText: ADD_DOCUMENT_MODAL_COPY.confirmButtonText,
      showSort: true,
      showAdvancedFilter: false,
      showSelectAll: true,
      showViewAction: true,
      orderOptions: ADD_DOCUMENT_ORDER_OPTIONS,
      defaultAmount: ADD_DOCUMENT_DEFAULT_AMOUNT,
      get isSubmitting() {
        return isAddDocumentSubmitting.value;
      },
      get searchUrl() {
        return eligibleDocumentsSearchUrl.value ?? ADD_DOCUMENT_MODAL_COPY.fallbackSearchUrl;
      },
      onClose() {
        addDocumentModal.close();
      },
      async onView(revision: DocumentRevision) {
        await viewDocument(String(revision.documentId), undefined, revision.id);
      },
      'onSelect-multiple': async (revisions: DocumentRevision[]) => {
        const success = await handleAddDocumentSubmit(revisions);
        if (success) {
          addDocumentModal.close();
        }
      },
    },
  });

  const deleteConfirmationModal = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return deleteModalTitle.value;
      },
      get description() {
        return deleteModalDescription.value;
      },
      get isDeleting() {
        return isDeletingSelection.value;
      },
      onClose() {
        deleteConfirmationModal.close();
      },
      async onConfirm() {
        const success = await confirmDeleteSelection();
        if (success) {
          deleteConfirmationModal.close();
        }
      },
    },
  });

  const pastePermissionsConfirmationModal = useModal({
    component: ConfirmationModal,
    attrs: {
      title: PASTE_PERMISSIONS_CONFIRMATION_TITLE,
      text: PASTE_PERMISSIONS_CONFIRMATION_DESCRIPTION,
      actionButtonText: PASTE_PERMISSIONS_CONFIRM_BUTTON_TEXT,
      cancelButtonText: PASTE_PERMISSIONS_CANCEL_BUTTON_TEXT,
      onClose() {
        pastePermissionsConfirmationModal.close();
      },
      async onConfirm() {
        pastePermissionsConfirmationModal.close();
        await pastePermissions();
      },
    },
  });

  const openCreateModuleModal = () => {
    createModuleTitleError.value = '';
    createModuleModal.open();
  };

  const openCreateFolderModal = () => {
    if (!handleAddChild()) {
      return;
    }
    createFolderModal.open();
  };

  const openAddRecordModal = () => {
    if (!selectedNode.value) {
      return;
    }
    // The modal loads its own first page, so opening no longer waits on the whole eligible
    // universe. While that page is in flight the table shows its loading state, the same way
    // the document selection modal does.
    addRecordModal.open();
  };

  const openAddDocumentModal = () => {
    if (!selectedNode.value) {
      return;
    }
    addDocumentModal.open();
  };

  const openPastePermissionsModal = () => {
    if (!canPastePermissions.value || selectedNodeIds.value.length === 0) {
      return;
    }
    pastePermissionsConfirmationModal.open();
  };

  const openDeleteConfirmationModal = () => {
    if (selectedNodeIds.value.length === 0) {
      return;
    }
    deleteConfirmationModal.open();
  };

  return {
    openCreateModuleModal,
    openCreateFolderModal,
    openAddRecordModal,
    openAddDocumentModal,
    openDeleteConfirmationModal,
    openPastePermissionsModal,
  };
}
